'use client';

import React, { useEffect, useState } from "react";
import {
    Box, Button, Divider, Stack, Typography,
    Accordion, AccordionSummary, AccordionDetails,
    Card, CardContent, Paper, SwipeableDrawer,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MainMenuItem from "@/app/components/products/main_menu_item";
import { ProductMenuItem, Filter } from "@/store/productSlice";
import useScreenSize from "@/utils/useScreenSize";

interface ShopByMenuIslandProps {
    mainMenuFilters: ProductMenuItem[];
    productType: string;
    brand: string;
    cssStyles: { [key: string]: string };
}

/**
 * Client island for the "Shop By" menu panel.
 * Desktop: inline popover menu items. Mobile: SwipeableDrawer.
 */
const ShopByMenuIsland: React.FC<ShopByMenuIslandProps> = ({
    mainMenuFilters,
    productType,
    brand,
    cssStyles,
}) => {
    const screenSize = useScreenSize();
    const [isClient, setIsClient] = useState(false);
    const [openShopByMobile, setOpenShopByMobile] = useState(false);
    const [shopByExpandedIndex, setShopByExpandedIndex] = useState<number | null>(0);

    useEffect(() => { setIsClient(true); }, []);

    function getLink(type: string, name: string): string {
        if (brand === "nestingale") {
            return `/products/${productType}/${type}/${name}`;
        } else {
            return `/${brand}/products/${productType}/${type}/${name}`;
        }
    }

    const toggleShopByMobile = (newOpen: boolean) => () => {
        setOpenShopByMobile(newOpen);
        setShopByExpandedIndex(0);
    };

    if (!mainMenuFilters || mainMenuFilters.length === 0) return null;

    return (
        <Box component='div'>
            <Divider />
            <Stack direction="row" sx={{ py: 1, alignItems: 'center' }} spacing={2}>
                {isClient && screenSize.width > 768 ? (
                    <>
                        <Typography className={cssStyles.sub_heading}>SHOP BY</Typography>
                        {mainMenuFilters.map((mmf: ProductMenuItem) => (
                            <MainMenuItem key={"mainMenuFilter-" + mmf.name} {...mmf} />
                        ))}
                    </>
                ) : (
                    <>
                        <Box component="div" sx={{ display: 'flex', width: '100%' }}>
                            <Button onClick={toggleShopByMobile(true)} className={cssStyles.shopby_cta_mweb}>Shop By</Button>
                        </Box>
                        <SwipeableDrawer
                            anchor="left"
                            open={openShopByMobile}
                            onClose={toggleShopByMobile(false)}
                            onOpen={toggleShopByMobile(true)}
                            sx={{ zIndex: '1201', '.MuiDrawer-paper': { width: '70vw' } }}
                        >
                            <div className={cssStyles.filter_mobile_header}>
                                <Box component="div" className={cssStyles.filter_by_label}>
                                    <Typography className={cssStyles.sub_heading}>SHOP BY</Typography>
                                </Box>
                                <Button color="secondary" className={cssStyles.filter_close_cta} onClick={toggleShopByMobile(false)}>Close</Button>
                            </div>
                            {mainMenuFilters.map((mmf: ProductMenuItem, index: number) => (
                                <Accordion
                                    expanded={shopByExpandedIndex === index}
                                    onChange={(_e: any, exp: any) => setShopByExpandedIndex(exp ? index : null)}
                                    key={"shopby-" + mmf.name}
                                    slotProps={{ transition: { unmountOnExit: true } }}
                                    className={cssStyles.mobile_filter_drawer}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className={cssStyles.mobile_filter_label}
                                        sx={{ '.MuiAccordionSummary-content': { margin: '0px' } }}>
                                        {mmf.displayName}
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ maxHeight: 'calc(100vh - 186px)', overflow: 'auto' }}>
                                        {mmf?.items?.map((cardItem: Filter) => {
                                            if (cardItem.name === "multicolor") {
                                                cardItem.image = "https://d221a61rb87vel.cloudfront.net/catalog-assets/icons/multicolor-icon.png";
                                                cardItem.hexaCode = "";
                                            }
                                            return (
                                                <Card key={mmf.name + cardItem.name} sx={{ marginBottom: '10px' }}>
                                                    <a id={cardItem.id || cardItem.name || cardItem.displayName} href={getLink(mmf.name, cardItem.name)}>
                                                        <Box component='div'>
                                                            <CardContent sx={{ padding: '8px' }}>
                                                                {cardItem.hexaCode
                                                                    ? <Paper sx={{ height: "100px", width: "100%", borderRadius: "0", backgroundColor: cardItem.hexaCode }} />
                                                                    : <img src={cardItem.image} className={cssStyles.shopby_mweb_img} />}
                                                            </CardContent>
                                                            <Typography textAlign={"center"} noWrap={true} gutterBottom variant="h6" component="div">
                                                                {cardItem.displayName || cardItem.name}
                                                            </Typography>
                                                        </Box>
                                                    </a>
                                                </Card>
                                            );
                                        })}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </SwipeableDrawer>
                    </>
                )}
            </Stack>
            <Divider />
        </Box>
    );
};

export default ShopByMenuIsland;
